import { Avatar, TierBadge } from '@busy/ui';
import type { ClientRow, Tier } from '@busy/api';
import { BigTipperBadge } from './BigTipperBadge';

// Apple-Contacts-style row: avatar, name, subtitle (phone or last visit
// hint), small chevron when not in selection mode. Selection mode swaps
// the chevron for a circular checkbox on the left.

export function CompactClientRow({
  client,
  photoUrl,
  tier,
  isBigTipper,
  subtitle,
  selectMode,
  selected,
  onToggleSelect,
  onOpen,
  language,
}: {
  client: ClientRow;
  photoUrl?: string;
  tier?: Tier;
  /** Optional Big Tipper flag — true when this client is top 10% of
   *  tippers on the user's roster (client_stats.is_big_tipper from
   *  migration 0066). Callers that don't have stats handy can omit. */
  isBigTipper?: boolean;
  /** Right-side subtitle line — phone, last visit, or whatever the parent decides. */
  subtitle?: string;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
  language: string;
}) {
  const fullName = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim();
  const displayName = fullName || client.phone || '—';
  // i18n-aware locale for any subtitle date formatting the caller might
  // request elsewhere — here we just use it as the row aria suffix.
  const langLabel = language;
  const handleClick = () => {
    if (selectMode) onToggleSelect();
    else onOpen();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={selectMode ? selected : undefined}
      lang={langLabel}
      className={`w-full flex items-center gap-3 pl-4 pr-7 py-2.5 text-left transition-colors ${
        selected ? 'bg-gold-500/10 dark:bg-gold-500/15' : 'bg-surface hover:bg-bg-alt'
      }`}
    >
      {selectMode ? (
        <span
          aria-hidden
          className={`shrink-0 w-6 h-6 rounded-full grid place-items-center border-2 transition-colors ${
            selected
              ? 'border-gold-500 bg-gold-500 text-navy-900'
              : 'border-line bg-surface'
          }`}
        >
          {selected && <CheckIcon className="w-4 h-4" />}
        </span>
      ) : null}
      <Avatar name={displayName} size="md" tier={tier} photoUrl={photoUrl} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <strong className="text-[15px] truncate">{displayName}</strong>
          {tier && <TierBadge tier={tier} size="sm" label={tier} />}
          {isBigTipper && <BigTipperBadge size="sm" />}
        </div>
        {subtitle && <div className="text-[12px] text-ink-500 truncate">{subtitle}</div>}
      </div>
      {!selectMode && <ChevronRightIcon className="w-4 h-4 text-ink-500 shrink-0" />}
    </button>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
